import { useCallback, useEffect, useMemo, useState } from "react";
import {
	ClientMessagingOptions,
	ClientMessagingSettings,
	getClientMessagingOptions,
	getClientMessagingSettings,
	readClientErrors,
	updateClientMessagingSettings,
} from "../../../api/client-messaging-settings";
import {
	applyDraftChange,
	buildUpdateRequest,
	MessagingDraft,
	MessagingDraftError,
	toDraft,
	validateDraft,
} from "./messagingDraft";

export interface ClientMessagingSettingsForm {
	options: ClientMessagingOptions | null;
	draft: MessagingDraft | null;
	/** Checked before saving; shown next to the setting they concern. */
	errors: MessagingDraftError[];
	/** The API's reasons when it refused the last save. */
	serverErrors: string[];
	/** The admin changed something; only then is there anything to validate or save. */
	isDirty: boolean;
	change: (patch: Partial<MessagingDraft>) => void;
	/** Back to the saved settings — on cancel. */
	reset: () => void;
	/** Saves what changed; true when there was nothing to save or it saved. */
	save: () => Promise<boolean>;
}

/**
 * One client's messaging settings (KAN-145) as an editable form: loads the deployment's options and
 * the client's settings, keeps one draft object, and saves only what changed through
 * `PUT api/client-messaging-settings`.
 */
export const useClientMessagingSettings = (
	sellerId: number | null
): ClientMessagingSettingsForm => {
	const [options, setOptions] = useState<ClientMessagingOptions | null>(null);
	const [saved, setSaved] = useState<ClientMessagingSettings | null>(null);
	const [draft, setDraft] = useState<MessagingDraft | null>(null);
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	useEffect(() => {
		if (sellerId === null) return;

		let isCancelled = false;

		Promise.all([
			getClientMessagingOptions(),
			getClientMessagingSettings([sellerId]),
		])
			.then(([loadedOptions, [loadedSettings]]) => {
				if (isCancelled || !loadedSettings) return;
				setOptions(loadedOptions);
				setSaved(loadedSettings);
				setDraft(toDraft(loadedSettings));
				setServerErrors([]);
			})
			.catch(() => {
				// The axios interceptor already toasted it; the section stays hidden.
			});

		return () => {
			isCancelled = true;
		};
	}, [sellerId]);

	const errors = useMemo(
		() => (draft && options ? validateDraft(draft, options) : []),
		[draft, options]
	);

	const request = useMemo(
		() =>
			saved && draft
				? buildUpdateRequest(saved.sellerId, toDraft(saved), draft)
				: null,
		[saved, draft]
	);

	const change = useCallback(
		(patch: Partial<MessagingDraft>) => {
			if (!options) return;
			setDraft((current) =>
				current ? applyDraftChange(current, patch, options) : current
			);
			setServerErrors([]);
		},
		[options]
	);

	const reset = useCallback(() => {
		setDraft(saved ? toDraft(saved) : null);
		setServerErrors([]);
	}, [saved]);

	const save = useCallback(async (): Promise<boolean> => {
		if (!request) return true;

		try {
			const response = await updateClientMessagingSettings(request);
			const [updated] = response.settings;
			if (updated) {
				setSaved(updated);
				setDraft(toDraft(updated));
			}
			setServerErrors([]);
			return true;
		} catch (error: unknown) {
			setServerErrors(readClientErrors(error));
			return false;
		}
	}, [request]);

	return {
		options,
		draft,
		errors,
		serverErrors,
		isDirty: request !== null,
		change,
		reset,
		save,
	};
};
